import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface ModalErrorState {
  message: string;
  code?: string;
  details?: string[];
}

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (isOpen) {
      <div [ngClass]="['fixed inset-0 overflow-y-auto font-sans', zIndex]">
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity" (click)="close.emit()"></div>

        <!-- Dialog Container -->
        <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <div [ngClass]="['relative transform overflow-hidden rounded-2xl bg-white border border-slate-200 text-left shadow-2xl transition-all sm:my-8 sm:w-full text-slate-900', maxWidth]">
            <!-- Header -->
            <div class="flex items-center justify-between border-b border-slate-200 px-6 py-4 bg-slate-50">
              <h3 class="text-base font-extrabold text-slate-900 tracking-wide flex items-center gap-2">
                {{ title }}
              </h3>
              <button
                (click)="close.emit()"
                class="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-900 transition-colors"
              >
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Error / Notice Banner inside Modal -->
            @if (errorMessage || errorState) {
              @if (isInstitutionPending()) {
                <div class="mx-6 mt-4 p-4 rounded-2xl bg-emerald-50 border-2 border-emerald-300 text-emerald-950 text-xs space-y-2 shadow-md shadow-emerald-600/10">
                  <div class="flex items-start justify-between font-bold gap-3">
                    <div class="flex items-start gap-2.5">
                      <div class="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 mt-0.5 border border-emerald-300">
                        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div class="space-y-1">
                        <div class="text-xs font-black text-emerald-950 uppercase tracking-wide">
                          Institution Registration Pending Approval
                        </div>
                        <p class="text-xs font-medium text-emerald-800 leading-relaxed">{{ errorMessage || errorState?.message }}</p>
                      </div>
                    </div>
                    @if (errorCode || errorState?.code) {
                      <span class="font-mono text-[10px] px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 border border-emerald-300 font-extrabold shrink-0">
                        {{ errorCode || errorState?.code }}
                      </span>
                    }
                  </div>

                  @if ((errorDetails && errorDetails.length > 0) || (errorState && errorState.details && errorState.details.length > 0)) {
                    <ul class="mt-2 text-xs space-y-1 bg-white/90 border border-emerald-200 rounded-xl p-2.5 font-mono text-emerald-800">
                      @for (detail of (errorDetails || errorState?.details || []); track detail) {
                        <li class="flex items-start gap-1.5">
                          <span class="text-emerald-500 font-bold select-none">•</span>
                          <span class="break-all">{{ detail }}</span>
                        </li>
                      }
                    </ul>
                  }
                </div>
              } @else {
                <div class="mx-6 mt-4 p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-900 text-xs space-y-2 shadow-2xs">
                  <div class="flex items-center justify-between font-bold gap-2">
                    <div class="flex items-center gap-2">
                      <svg class="w-4 h-4 text-rose-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{{ errorMessage || errorState?.message }}</span>
                    </div>
                    @if (errorCode || errorState?.code) {
                      <span class="font-mono text-[10px] px-2 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-300 font-extrabold shrink-0">
                        {{ errorCode || errorState?.code }}
                      </span>
                    }
                  </div>

                  @if ((errorDetails && errorDetails.length > 0) || (errorState && errorState.details && errorState.details.length > 0)) {
                    <ul class="mt-2 text-xs space-y-1 bg-white/80 border border-rose-200 rounded-xl p-2.5 font-mono text-rose-800">
                      @for (detail of (errorDetails || errorState?.details || []); track detail) {
                        <li class="flex items-start gap-1.5">
                          <span class="text-rose-500 font-bold select-none">•</span>
                          <span class="break-all">{{ detail }}</span>
                        </li>
                      }
                    </ul>
                  }
                </div>
              }
            }

            <!-- Body -->
            <div class="p-6">
              <ng-content></ng-content>
            </div>
          </div>
        </div>
      </div>
    }
  `
})
export class ModalComponent {
  @Input() isOpen = false;
  @Input() title = '';
  @Input() maxWidth: string = 'max-w-2xl';
  @Input() zIndex: string = 'z-50';
  @Input() errorMessage?: string | null;
  @Input() errorCode?: string | null;
  @Input() errorDetails?: string[] | null;
  @Input() errorState?: ModalErrorState | null;
  @Output() close = new EventEmitter<void>();

  isInstitutionPending(): boolean {
    const code = this.errorCode || this.errorState?.code;
    if (code === 'ZANX-1011') return true;
    const msg = (this.errorMessage || this.errorState?.message || '').toLowerCase();
    return msg.includes('institution') && (
      msg.includes('approv') ||
      msg.includes('activat') ||
      msg.includes('pending') ||
      msg.includes('not yet')
    );
  }
}
